import React, { useState, useEffect, useRef } from 'react';
import './Leaderboard.css';
import { leaderboardApi, userApi } from '../api';
import { Category } from '../types/game';
import { LeaderboardEntry } from '../types/leaderboard';
import { useLocation } from 'react-router-dom';

const TOP_LEADERBOARD_ENTRIES = 10;

const Leaderboard: React.FC = () => {
  const location = useLocation();
  const categoryFromGameOverScreen = location.state?.category || 'Total Score';

  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentCategory, setCurrentCategory] = useState<string>(categoryFromGameOverScreen);
  const [categories, setCategories] = useState<Category[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    const fetchCategories = async () => {
      try {
        const categoriesResponse = await leaderboardApi.getLeaderboardCategories();
        if (categoriesResponse.data.status === 'success') {
          setCategories(categoriesResponse.data.categories);
        }
      } catch (err) {
        console.error('Failed to load categories');
      }
    };

    const fetchData = async () => {
      try {
        const leaderboardResponse = await leaderboardApi.getLeaderboard(currentCategory);
        if (leaderboardResponse.data.status === 'success') {
          setLeaderboard(leaderboardResponse.data.leaderboard);
        } else {
          setError('Failed to load leaderboard');
        }
      } catch (err) {
        setError('Failed to load leaderboard');
      } finally {
        setIsLoading(false);
      }
    };

    fetchCategories();
    fetchData();
  }, [currentCategory]);

  const handleCategoryChange = (categoryName: string) => {
    setCurrentCategory(categoryName);
    setIsDropdownOpen(false);
  };

  if (isLoading) {
    return <div className="leaderboard-container">Loading...</div>;
  }

    if (error) {
      return <div className="leaderboard-container error">{error}</div>;
    }

  const selectedCategoryName = categories.find(cat => cat.name === currentCategory)?.name || currentCategory;

  return (
    <div className="leaderboard-container">
      <h1 className="leaderboard-title">AI Showdown Leaderboard</h1>
      
      <div className="leaderboard-category-selector">
        <div className={`dropdown ${isDropdownOpen ? 'open' : ''}`} ref={dropdownRef}>
          <div className="dropdown-button" onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
            {selectedCategoryName}
            <span className="dropdown-arrow">▼</span>
          </div>
          {isDropdownOpen && (
            <div className="dropdown-content">
              {categories.map((category) => (
                <div 
                  key={category.name} 
                  className={`dropdown-item ${category.name === currentCategory ? 'active' : ''}`}
                  onClick={() => handleCategoryChange(category.name)}
                >
                  {category.name}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      
      <div className="leaderboard-table">
        <div className="leaderboard-header">
          <div className="rank">#</div>
          <div className="username">Challenger</div>
          <div className="score">Score</div>
        </div>
        {leaderboard.slice(0, TOP_LEADERBOARD_ENTRIES).map((entry, index) => (
          entry.score ? (
            <div key={index} className="leaderboard-row">
              <div className="rank">{(entry.order !== undefined && entry.order !== -1) ? entry.order : index + 1}</div>
              <div className="username truncate">{entry.username}</div>
              <div className="score">{entry.score[currentCategory]}</div>
            </div>
          ) : null
        ))}
        {/* Here we use the TOP_LEADERBOARD_ENTRIES as index, as the user is the last element, if they're not in TOP_LEADERBOARD_ENTRIES, they're not in the leaderboard */}
        {leaderboard.length > TOP_LEADERBOARD_ENTRIES && (
          <>
            <div className="leaderboard-divider">
              <span>More Entries</span>
            </div>
            {leaderboard[TOP_LEADERBOARD_ENTRIES].score && (
              <div className="leaderboard-row">
                <div className="rank">{leaderboard[TOP_LEADERBOARD_ENTRIES].order !== undefined ? leaderboard[TOP_LEADERBOARD_ENTRIES].order : 11}</div>
                <div className="username truncate">{leaderboard[TOP_LEADERBOARD_ENTRIES].username}</div>
                <div className="score">{leaderboard[TOP_LEADERBOARD_ENTRIES].score[currentCategory]}</div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Leaderboard; 